/**
 * Chat Message Bubble - Single message with text + clickable images
 */

import { useTranslation } from 'react-i18next';

const KNOWN_RATIOS = [
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 },
  { label: '16:9', value: 16 / 9 },
  { label: '9:16', value: 9 / 16 },
  { label: '4:5', value: 4 / 5 },
  { label: '5:4', value: 5 / 4 },
  { label: '2:3', value: 2 / 3 },
  { label: '3:2', value: 3 / 2 },
  { label: '21:9', value: 21 / 9 },
  { label: '1:4', value: 1 / 4 },
  { label: '4:1', value: 4 },
  { label: '1:8', value: 1 / 8 },
  { label: '8:1', value: 8 },
];

function detectAspectRatio(w: number, h: number): string {
  if (!w || !h) return '1:1';
  const ratio = w / h;
  let closest = KNOWN_RATIOS[0];
  let minDiff = Infinity;
  for (const r of KNOWN_RATIOS) {
    const diff = Math.abs(ratio - r.value);
    if (diff < minDiff) { minDiff = diff; closest = r; }
  }
  return closest.label;
}
import { User, Bot, Check } from 'lucide-react';
import { ChatImageActions } from './ChatImageActions';
import type { ChatMessage } from '@/lib/api/chat-image';

interface Props {
  message: ChatMessage;
  conversationId: string;
  selectedImageIds?: string[];
  onToggleImage?: (imageId: string, url: string, aspectRatio?: string) => void;
}

export function ChatMessageBubble({ message, conversationId, selectedImageIds = [], onToggleImage }: Props) {
  const { t } = useTranslation();
  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-3 px-4 py-3 ${isUser ? 'justify-end' : ''}`}>
      {!isUser && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900">
          <Bot className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        </div>
      )}
      <div className={`max-w-[75%] ${isUser ? 'order-first' : ''}`}>
        <MessageContent
          message={message}
          conversationId={conversationId}
          selectedImageIds={selectedImageIds}
          onToggleImage={onToggleImage}
        />
        {!isUser && message.credits_charged > 0 && (
          <div className="text-xs text-neutral-400 mt-1">
            {message.credits_charged.toFixed(2)} {t('chatImage.credits')}
          </div>
        )}
      </div>
      {isUser && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-200 dark:bg-neutral-700">
          <User className="h-4 w-4 text-neutral-600 dark:text-neutral-300" />
        </div>
      )}
    </div>
  );
}

interface ContentProps {
  message: ChatMessage;
  conversationId: string;
  selectedImageIds: string[];
  onToggleImage?: (imageId: string, url: string, aspectRatio?: string) => void;
}

function MessageContent({ message, conversationId, selectedImageIds, onToggleImage }: ContentProps) {
  const isUser = message.role === 'user';

  return (
    <div
      className={`rounded-2xl px-4 py-2.5 ${
        isUser
          ? 'bg-neutral-800 dark:bg-neutral-700 text-white'
          : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100'
      }`}
    >
      {message.text_content && (
        <p className="text-sm whitespace-pre-wrap">{message.text_content}</p>
      )}
      {message.image_refs.length > 0 && (
        <div className="mt-2 space-y-2">
          {message.image_refs.map((img) => {
            const isSelected = selectedImageIds.includes(img.image_id);
            return (
              <div key={img.image_id}>
                <button
                  type="button"
                  onClick={(e) => {
                    const imgEl = e.currentTarget.querySelector('img');
                    const ratio = imgEl ? detectAspectRatio(imgEl.naturalWidth, imgEl.naturalHeight) : undefined;
                    onToggleImage?.(img.image_id, img.url, ratio);
                  }}
                  className={`relative block rounded-lg overflow-hidden transition-all ${
                    isSelected ? 'ring-3 ring-primary-500 ring-offset-2' : 'hover:ring-2 hover:ring-primary-300'
                  }`}
                >
                  <img
                    src={img.url}
                    alt="Generated"
                    className="rounded-lg max-w-full max-h-80 object-contain"
                  />
                  {isSelected && (
                    <div className="absolute top-2 right-2 rounded-full bg-primary-500 p-1">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                  )}
                </button>
                <ChatImageActions
                  imageUrl={img.url}
                  imageId={img.image_id}
                  conversationId={conversationId}
                  messageId={message.id}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
